import type { CognitoIdToken, CognitoUser } from 'amazon-cognito-identity-js';
import { Amplify, Auth } from 'aws-amplify';
import {
  type FC,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AwsConfigAuth } from '../../../modules/config';
import {
  AuthContext,
  type CognitoCredential,
} from '../../../modules/context/authContext';
import { AuthStatus } from '../../../modules/enums/status';
import { useRequest } from '../../../modules/hooks';
import { endpoints, routes } from '../../../modules/mappers/urls';

Amplify.configure({ Auth: AwsConfigAuth });

export const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const [credentialsInfo, setCredentialsInfo] = useState<CognitoCredential | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [cognitoToken, setCognitoToken] = useState<string>('');
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState<CognitoUser | null>(null);
  const [authStatus, setAuthStatus] = useState<AuthStatus>(AuthStatus.pending);

  const getUserPermissionsUrl = `${endpoints.USER_PERMISSIONS}`;
  const [{ data: permissionsResult, loading: permissionsLoading }, getUserPermissions] =
    useRequest<string[]>(
      getUserPermissionsUrl,
      'get',
      { authToken: cognitoToken },
      { manual: true },
    );

  const configureCredential = useCallback(
    (idToken: CognitoIdToken) => {
      setCredentialsInfo({
        token: idToken.getJwtToken(),
        email: idToken.payload.email,
        name: `${idToken.payload.given_name} ${idToken.payload.family_name}`,
        permissions,
      });
    },
    [setCredentialsInfo],
  );

  const handleSignOut = useCallback(async () => {
    await Auth.signOut();
    setUser(null);
    setCredentialsInfo(null);

    navigate(routes.LOGIN, {});
  }, [navigate, setCredentialsInfo]);

  useEffect(() => {
    if (!cognitoToken || !credentialsInfo) return;

    getUserPermissions({ params: { email: credentialsInfo.email } });
  }, [cognitoToken]);

  useEffect(() => {
    if (!permissionsResult || !permissionsResult.result.length) return;

    setPermissions(permissionsResult.result);
  }, [permissionsResult]);

  const handleSignIn = useCallback(
    async (username: string, password: string) => {
      const cognitoUser = await Auth.signIn(username, password);
      const cognitoSession = await Auth.currentSession();
      const cognitoIdToken = cognitoSession.getIdToken();

      const origin = location.state?.from?.pathname
        ? `${location.state?.from?.pathname}${location.state?.from?.search || ''}`
        : routes.HOME;

      configureCredential(cognitoIdToken);
      setCognitoToken(cognitoIdToken.getJwtToken());
      setUser(cognitoUser);
      setAuthStatus(AuthStatus.signedIn);
      navigate(origin);
    },
    [
      configureCredential,
      setUser,
      setAuthStatus,
      navigate,
      location.state?.from?.pathname,
    ],
  );

  const handleChangePassword = useCallback(
    async (oldPassword: string, newPassword: string) => {
      if (authStatus === AuthStatus.signedIn) {
        await Auth.changePassword(user, oldPassword, newPassword);
      }
    },
    [authStatus, user],
  );

  const handleForgotPassword = useCallback(async (username: string) => {
    await Auth.forgotPassword(username);
  }, []);

  const handleForgotPasswordSubmit = useCallback(
    async (username: string, code: string, newPassword: string) => {
      await Auth.forgotPasswordSubmit(username, code, newPassword);
    },
    [],
  );

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then((result) => {
        setUser(result);
        setAuthStatus(AuthStatus.signedIn);
        Auth.currentSession().then((sessionResult) => {
          const idToken = sessionResult.getIdToken();
          configureCredential(idToken);
          setCognitoToken(idToken.getJwtToken());
        });
      })
      .catch(() => {
        setUser(null);
        setAuthStatus(AuthStatus.signedOut);
      });
  }, [handleSignOut, setCredentialsInfo, configureCredential]);

  const value = useMemo(() => {
    return {
      authStatus,
      credentialsInfo,
      permissions,
      permissionsLoading,
      signIn: handleSignIn,
      signOut: handleSignOut,
      forgotPassword: handleForgotPassword,
      forgotPasswordSubmit: handleForgotPasswordSubmit,
      changePassword: handleChangePassword,
    };
  }, [
    authStatus,
    credentialsInfo,
    permissions,
    permissionsLoading,
    handleSignIn,
    handleSignOut,
    handleForgotPassword,
    handleForgotPasswordSubmit,
    handleChangePassword,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
